import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  ComposedChart, Line, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { getAuthenticatedAxios, getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const fmtTime = (t) => new Date(t).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const fmtPrice = (p) => {
  if (!p) return '$0';
  if (p >= 1) return `$${p.toFixed(4)}`;
  // десятичный формат (не экспонента), с обрезкой хвостовых нулей
  const str = p.toFixed(9).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  return `$${str}`;
};
const fmtTokens = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : Math.round(n || 0).toString());
const fmtPct = (p) => `${p > 0 ? '+' : ''}${p}%`;
const fmtUsd = (v) => `${v > 0 ? '+' : ''}$${Math.abs(v) >= 1000 ? (v / 1000).toFixed(1) + 'K' : v.toFixed(2)}`;

const EnemyDot = ({ cx, cy, payload }) => {
  if (cx == null || cy == null) return null;
  const fill = payload.in_profit ? '#16a34a' : '#dc2626';
  return <circle cx={cx} cy={cy} r={payload.r || 4} fill={fill} fillOpacity={0.5} stroke={fill} strokeWidth={1} />;
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const pt = payload.find((p) => p.payload && p.payload.wallet);
  if (pt) {
    const e = pt.payload;
    const pc = e.in_profit ? 'text-green-600' : 'text-red-600';
    return (
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2 shadow-md text-xs min-w-[210px]">
        <div className="font-semibold mb-1">Враг <span className="font-mono">{e.wallet_short}</span></div>
        <div className={`text-sm font-bold ${pc}`}>P/L сейчас: {fmtPct(e.pnl_pct)} ({fmtUsd(e.pnl_usd)})</div>
        <div className="text-gray-600 mt-1">средний вход: <b>{fmtPrice(e.price)}</b></div>
        <div className="text-gray-600">держит: <b>{fmtTokens(e.position)}</b> (вложил ${e.usd_invested})</div>
        <div className="text-gray-500">покупок: {e.buy_count} · вход ≈ {fmtTime(e.t)}</div>
      </div>
    );
  }
  const p = payload[0];
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      <div className="text-gray-600">{fmtTime(p.payload.t)}</div>
      <div className="font-semibold">{fmtPrice(p.payload.price)}</div>
    </div>
  );
};

const POS_FILTERS = [
  { label: 'все', v: 1 },
  { label: '100K+', v: 100000 },
  { label: '1M+', v: 1000000 },
  { label: '5M+', v: 5000000 },
];

const EnemyEntriesChart = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [holdMin, setHoldMin] = useState(1);
  const [logScale, setLogScale] = useState(false);
  const [showOverhang, setShowOverhang] = useState(true);
  const [mode, setMode] = useState('avg');

  const load = useCallback(async (min, m) => {
    setLoading(true); setErr(null);
    try {
      const api = getAuthenticatedAxios();
      const res = await api.get(`${API_URL}/api/market-maker/enemy-entries?hold_min_tokens=${min}&mode=${m}`);
      setData(res.data);
    } catch (e) { setErr(e?.message || 'Ошибка загрузки'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(holdMin, mode); }, [load, holdMin, mode]);

  const priceData = (data?.price_series || []).map((p) => ({ t: new Date(p.t).getTime(), price: p.price }));
  const rawEntries = (data?.enemy_entries || []).map((e) => ({ ...e, t: new Date(e.t).getTime() }));
  const maxPos = Math.max(1, ...rawEntries.map((e) => e.position || 0));
  const entryData = rawEntries.map((e) => ({ ...e, r: 4 + 9 * Math.sqrt((e.position || 0) / maxPos) }));
  const stats = data?.stats || {};
  const cur = data?.current_price || 0;
  const avg = data?.avg_entry_price || 0;
  const sym = data?.token_symbol || 'TOKEN';
  // общий нереализованный P/L держателей vs текущая цена (навес в $)
  const overhangPnlPct = avg > 0 ? Math.round((cur / avg - 1) * 1000) / 10 : 0;

  const allY = [...priceData.map((d) => d.price), ...entryData.map((d) => d.price)].filter((x) => x > 0);
  const yMin = allY.length ? Math.min(...allY) : 0;
  const yMax = allY.length ? Math.max(...allY) : 1;
  const yDomain = logScale ? [yMin * 0.85, yMax * 1.15] : [0, yMax * 1.1];

  const ctlBtn = (active) => `px-2 py-0.5 rounded text-xs transition-all ${active ? 'bg-indigo-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>📈 Цена {sym} и входы держащих врагов</span>
          <button onClick={() => load(holdMin, mode)} className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700">⟳ обновить</button>
        </CardTitle>
        <CardDescription>
          {mode === 'avg'
            ? <>1 точка = 1 враг (из тех, кто <b>ещё держит</b>) на его <b>среднем входе</b>.</>
            : <>каждая точка = <b>отдельная покупка</b> держащего врага.</>} 🟢 в плюсе / 🔴 в минусе, размер ∝ позиции. Наведи — P/L сейчас.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-3 text-xs">
          <span className="px-2 py-1 rounded bg-blue-50 text-blue-700">цена сейчас: <b>{fmtPrice(cur)}</b></span>
          <span className="px-2 py-1 rounded bg-amber-50 text-amber-700">ср. вход (навес): <b>{fmtPrice(avg)}</b> <span className={overhangPnlPct >= 0 ? 'text-green-600' : 'text-red-600'}>({fmtPct(overhangPnlPct)})</span></span>
          <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700">держат: <b>{stats.still_holding ?? '—'}</b></span>
          <span className="px-2 py-1 rounded bg-gray-100 text-gray-600">из <b>{stats.enemy_buyers ?? '—'}</b> покупавших</span>
          <span className="px-2 py-1 rounded bg-red-50 text-red-600">сдампили: <b>{stats.dumped ?? '—'}</b></span>
          <span className="px-2 py-1 rounded bg-gray-100 text-gray-600">токенов у них: <b>{fmtTokens(stats.tokens_held_by_holders || 0)}</b></span>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4 text-xs border-y border-gray-100 py-2">
          <div className="flex items-center gap-1">
            <span className="text-gray-500">режим:</span>
            <button className={ctlBtn(mode === 'avg')} onClick={() => setMode('avg')}>ср. цена</button>
            <button className={ctlBtn(mode === 'all')} onClick={() => setMode('all')}>все входы</button>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">мин. позиция:</span>
            {POS_FILTERS.map((f) => (
              <button key={f.v} className={ctlBtn(holdMin === f.v)} onClick={() => setHoldMin(f.v)}>{f.label}</button>
            ))}
          </div>
          <button className={ctlBtn(logScale)} onClick={() => setLogScale((v) => !v)}>лог-шкала: {logScale ? 'вкл' : 'выкл'}</button>
          <button className={ctlBtn(showOverhang)} onClick={() => setShowOverhang((v) => !v)}>линия навеса: {showOverhang ? 'вкл' : 'выкл'}</button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-400">Загрузка графика…</div>
        ) : err ? (
          <div className="text-center py-16 text-red-500">{err}</div>
        ) : priceData.length === 0 ? (
          <div className="text-center py-16 text-gray-400">Нет данных по свопам пока.</div>
        ) : (
          <ResponsiveContainer width="100%" height={460}>
            <ComposedChart margin={{ top: 10, right: 18, bottom: 10, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="t" type="number" scale="time" domain={['dataMin', 'dataMax']} tickFormatter={fmtTime} tick={{ fontSize: 11, fill: '#888' }} minTickGap={60} />
              <YAxis dataKey="price" type="number" scale={logScale ? 'log' : 'linear'} domain={yDomain} allowDataOverflow tickFormatter={fmtPrice} tick={{ fontSize: 11, fill: '#888' }} width={88} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={cur} stroke="#3b82f6" strokeDasharray="4 4" strokeOpacity={0.6}
                label={{ value: `сейчас ${fmtPrice(cur)}`, position: 'right', fontSize: 10, fill: '#3b82f6' }} />
              {showOverhang && avg > 0 && (
                <ReferenceLine y={avg} stroke="#d97706" strokeDasharray="6 3" strokeWidth={1.5}
                  label={{ value: `ср. вход ${fmtPrice(avg)}`, position: 'insideTopRight', fontSize: 10, fill: '#d97706' }} />
              )}
              <Line data={priceData} dataKey="price" type="monotone" stroke="#3b82f6" strokeWidth={1.5} dot={false} isAnimationActive={false} name="Цена" />
              <Scatter data={entryData} dataKey="price" shape={<EnemyDot />} isAnimationActive={false} name="Враг-держатель" />
            </ComposedChart>
          </ResponsiveContainer>
        )}
        <div className="mt-2 text-[11px] text-gray-400">
          Входы с момента отслеживания пула. Цена — из ценового сервиса (≈ DexScreener). Битые цены отфильтрованы.
        </div>
      </CardContent>
    </Card>
  );
};

export default EnemyEntriesChart;
